import React, { useCallback, useEffect, useState } from 'react';
import Add from '@mui/icons-material/Add';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import Remove from '@mui/icons-material/Remove';
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined';
import {
  Box,
  CardMedia,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { trackV2InitiateCheckout } from '../services/tracking';
import { useV2Session } from '../state/SessionProvider';
import { paths } from '../config/paths';
import { getProductUrlSlug } from '../utils/productUrl';

import noImage from '../assets/no-image.png';
import Button from '../components/Button';
import FreeGiftBadge from '../components/FreeGiftBadge';
import CartPageHeader from '../components/cart/CartPageHeader';
import CartSuggestions from '../components/cart/CartSuggestions';
import FreeDeliveryPromotion from '../components/cart/FreeDeliveryPromotion';
import PageContainer from '../layouts/PageContainer';
import { v2Colors } from '../design/theme';
import {
  clearCart,
  computeCartCustomizationTotal,
  formatCartCustomizationSummary,
  getCart,
  listCartCustomizationAddOns,
  setCartInstructions,
  setCartLineQuantity,
} from '../services/commerce';

const cardSx = {
  bgcolor: v2Colors.white,
  borderRadius: 3,
  p: { xs: 1.75, sm: 2 },
  mb: 2,
  boxShadow: 'none',
};

function CartLineRow({ line, onQtyChange, onOpenProduct, busy }) {
  const qty = Number(line.quantity) || 0;
  const unit = Number(line.price) || 0;
  const lineTotal = unit * qty;
  const addOns = listCartCustomizationAddOns(line.customizations);
  const tagLabels =
    addOns.length > 0
      ? addOns.map((a) => a.name)
      : formatCartCustomizationSummary(line.customizations)
        ? formatCartCustomizationSummary(line.customizations).split(' · ')
        : [];

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <CardMedia
          component="img"
          image={line.imageUrl || noImage}
          alt={line.name}
          sx={{
            width: 72,
            height: 72,
            flexShrink: 0,
            borderRadius: 2,
            objectFit: 'cover',
            bgcolor: v2Colors.surface,
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: '1.05rem',
              lineHeight: 1.25,
              color: v2Colors.heading,
              cursor: line.productId ? 'pointer' : 'default',
              mb: tagLabels.length ? 0.75 : 1,
            }}
            onClick={() => {
              const slug = line.urlId || getProductUrlSlug({ name: line.name });
              if (slug) onOpenProduct?.(slug);
            }}
          >
            {line.name}
            {line.isFreeSkuItem ? <FreeGiftBadge /> : null}
          </Typography>
          {tagLabels.length ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {tagLabels.map((tag) => (
                <Box
                  key={`${line.productId}-${tag}`}
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 1,
                    bgcolor: v2Colors.tagFill,
                    color: v2Colors.tagText,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          ) : null}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mt: 'auto',
            }}
          >
            <Typography fontWeight={700} sx={{ fontSize: '1.1rem', color: v2Colors.heading }}>
              ₹{lineTotal}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: v2Colors.surface,
                borderRadius: 10,
                px: 0.5,
                py: 0.25,
                minHeight: 36,
              }}
            >
              <Box
                component="button"
                type="button"
                aria-label="Decrease quantity"
                disabled={busy || qty <= 0}
                onClick={() => onQtyChange(line.productId, qty - 1)}
                sx={{
                  border: 'none',
                  bgcolor: 'transparent',
                  color: v2Colors.primary,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: busy || qty <= 0 ? 'not-allowed' : 'pointer',
                  opacity: busy || qty <= 0 ? 0.4 : 1,
                  fontFamily: 'inherit',
                }}
              >
                <Remove fontSize="small" />
              </Box>
              <Typography fontWeight={700} sx={{ minWidth: 24, textAlign: 'center', color: v2Colors.primary }}>
                {qty}
              </Typography>
              <Box
                component="button"
                type="button"
                aria-label="Increase quantity"
                disabled={busy}
                onClick={() => onQtyChange(line.productId, qty + 1)}
                sx={{
                  border: 'none',
                  bgcolor: 'transparent',
                  color: v2Colors.primary,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.4 : 1,
                  fontFamily: 'inherit',
                }}
              >
                <Add fontSize="small" />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        mb: bold ? 0 : 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: bold ? v2Colors.heading : v2Colors.textMuted,
          fontWeight: bold ? 700 : 400,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 600, color: v2Colors.heading }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function Cart() {
  const navigate = useNavigate();
  const { mobile, user } = useV2Session();
  const [cart, setCart] = useState({ products: [], totalAmount: 0, totalCount: 0 });
  const [instructionInput, setInstructionInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadCart = useCallback(async () => {
    const next = await getCart();
    setCart({
      products: Array.isArray(next.products) ? next.products : [],
      totalAmount: Number(next.totalAmount) || 0,
      totalCount: Number(next.totalCount) || 0,
    });
    setInstructionInput(typeof next.instructions === 'string' ? next.instructions : '');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadCart();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCart]);

  useEffect(() => {
    const onCartChanged = () => {
      loadCart();
    };
    window.addEventListener('v2-cart-changed', onCartChanged);
    return () => window.removeEventListener('v2-cart-changed', onCartChanged);
  }, [loadCart]);

  const hasItems = cart.products.length > 0 && cart.totalCount > 0;

  const handleQtyChange = async (productId, nextQty) => {
    if (!productId) return;
    setBusy(true);
    try {
      await setCartLineQuantity({ productId }, nextQty);
      await loadCart();
    } finally {
      setBusy(false);
    }
  };

  const handleClearAll = async () => {
    if (!hasItems || busy) return;
    if (!window.confirm('Remove all items from your bag?')) return;
    setBusy(true);
    try {
      await clearCart();
      await loadCart();
    } finally {
      setBusy(false);
    }
  };

  const goToProduct = (productSlug) => {
    if (productSlug) navigate(paths.product(productSlug));
  };

  const total = Number(cart.totalAmount) || 0;
  const customizationTotal = computeCartCustomizationTotal(cart.products);
  const itemSubtotal = Math.max(0, total - customizationTotal);

  if (loading) {
    return (
      <PageContainer sx={{ px: { xs: 1.5, sm: 2 } }}>
        <CartPageHeader clearDisabled />
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} sx={{ color: v2Colors.primary }} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ px: { xs: 1.5, sm: 2 }, pb: 4 }}>
      <CartPageHeader onClearAll={handleClearAll} clearDisabled={!hasItems || busy} />

      {!hasItems ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: 6,
            px: 2,
          }}
        >
          <ShoppingBagOutlined sx={{ fontSize: 56, color: v2Colors.textMuted, mb: 2 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: v2Colors.heading, mb: 1 }}>
            Your bag is empty
          </Typography>
          <Typography variant="body2" sx={{ color: v2Colors.textMuted, mb: 3, maxWidth: 280 }}>
            Add items from the catalog to see them here.
          </Typography>
          <Button
            onClick={() => navigate(paths.allProducts)}
            sx={{ borderRadius: 2, bgcolor: v2Colors.primary, '&:hover': { bgcolor: v2Colors.primaryDark } }}
          >
            Browse catalog
          </Button>
        </Box>
      ) : (
        <>
          {cart.products.map((line, index) => (
            <CartLineRow
              key={`${line.productId}-${line.isFreeSkuItem ? 'free' : 'line'}-${index}`}
              line={line}
              onQtyChange={handleQtyChange}
              onOpenProduct={goToProduct}
              busy={busy}
            />
          ))}

          <FreeDeliveryPromotion totalAmount={total} />
          <CartSuggestions cart={cart} />

          <TextField
            placeholder="Any special instructions? (e.g. ring bell twice)"
            fullWidth
            value={instructionInput}
            inputProps={{ maxLength: 100 }}
            onChange={(e) => setInstructionInput(e.target.value)}
            onBlur={() => {
              setCartInstructions(instructionInput).catch(() => {});
            }}
            disabled={busy}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: v2Colors.white,
                borderRadius: 2,
                '& fieldset': { borderColor: v2Colors.borderMuted },
                '&:hover fieldset': { borderColor: v2Colors.border },
                '&.Mui-focused fieldset': { borderColor: v2Colors.primary },
              },
            }}
          />

          <Box sx={cardSx}>
            <SummaryRow label="Subtotal" value={`₹${itemSubtotal}`} />
            {customizationTotal > 0 ? (
              <SummaryRow label="Customization" value={`₹${customizationTotal}`} />
            ) : null}
            <Divider sx={{ my: 1.5, borderColor: v2Colors.border }} />
            <SummaryRow label="Total" value={`₹${total}`} bold />
          </Box>

          <Button
            fullWidth
            size="large"
            disabled={busy}
            onClick={async () => {
              try {
                await setCartInstructions(instructionInput);
              } catch {
                // checkout reads cache when possible
              }
              trackV2InitiateCheckout({
                cart,
                session: { mobile, user },
              });
              navigate(paths.checkout);
            }}
            sx={{
              borderRadius: 2,
              py: 1.5,
              fontWeight: 700,
              fontSize: '1rem',
              bgcolor: v2Colors.primary,
              '&:hover': { bgcolor: v2Colors.primaryDark },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingBagOutlined sx={{ fontSize: 20 }} />
                Proceed to checkout
              </Box>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                ₹{total}
                <ArrowForwardRounded sx={{ fontSize: 20 }} />
              </Box>
            </Box>
          </Button>
        </>
      )}
    </PageContainer>
  );
}
